import React, { useMemo, useState, useCallback } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import {
  IconPlus,
  IconTrash,
  IconSparkles,
} from '@tabler/icons-react';
import { NODE_CATEGORIES } from '../../constants/nodeCategories';
import AgentsFlowService from '../../../services/AgentsFlowService';
import AnalyzeIntentModal from './AnalyzeIntentModal';
import useGraphDeclaredVariables from '../../hooks/useGraphDeclaredVariables';

/**
 * ConditionFields - Renders configuration fields for condition node types.
 *
 * Handles: intent, intent_router, time, data_check
 */
const ConditionFields = ({
  nodeKey,
  localConfig,
  handleConfigChange,
  renderDefaultPlaceholder,
  agentId,
  nodeId,
  nodes = [],
}) => {
  switch (nodeKey) {
    case 'intent':
      return (
        <>
          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Intenções a Detectar</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              className="afc-form-control"
              value={localConfig.intents || ''}
              onChange={(e) => handleConfigChange('intents', e.target.value)}
              placeholder="agendar, cancelar, duvida (uma por linha)"
            />
            <Form.Text className="afc-form-hint">
              Liste as intenções separadas por vírgula ou uma por linha
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Confiança Mínima (%)</Form.Label>
            <Form.Control
              type="number"
              className="afc-form-control"
              value={localConfig.minConfidence || 90}
              onChange={(e) => handleConfigChange('minConfidence', parseInt(e.target.value))}
              min={0}
              max={100}
            />
          </Form.Group>
        </>
      );

    case 'intent_router':
      return (
        <IntentRouterFields
          localConfig={localConfig}
          handleConfigChange={handleConfigChange}
          agentId={agentId}
          nodeId={nodeId}
        />
      );

    case 'time':
      return (
        <>
          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Horário de Início</Form.Label>
            <Form.Control
              type="time"
              className="afc-form-control"
              value={localConfig.startTime || '08:00'}
              onChange={(e) => handleConfigChange('startTime', e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Horário de Fim</Form.Label>
            <Form.Control
              type="time"
              className="afc-form-control"
              value={localConfig.endTime || '18:00'}
              onChange={(e) => handleConfigChange('endTime', e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Dias da Semana</Form.Label>
            <div className="afc-checkbox-group">
              {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((day, i) => (
                <Form.Check
                  key={day}
                  inline
                  type="checkbox"
                  id={`day-${i}`}
                  label={day}
                  checked={(localConfig.days || [1, 2, 3, 4, 5]).includes(i)}
                  onChange={(e) => {
                    const days = localConfig.days || [1, 2, 3, 4, 5];
                    if (e.target.checked) {
                      handleConfigChange('days', [...days, i].sort());
                    } else {
                      handleConfigChange('days', days.filter(d => d !== i));
                    }
                  }}
                />
              ))}
            </div>
          </Form.Group>
        </>
      );

    case 'data_check':
      return (
        <DataCheckFields
          localConfig={localConfig}
          handleConfigChange={handleConfigChange}
          nodes={nodes}
          currentNodeId={nodeId}
        />
      );

    default:
      return renderDefaultPlaceholder('condição');
  }
};

// =============================================================================
// IntentRouterFields — painel de configuração do Roteador Inteligente
// =============================================================================
//
// UX: admin escolhe em um único dropdown agrupado ("Capacidades" / "Ações")
// qual tipo de nó aquela rota representa. Se já houver uma rota do mesmo tipo,
// o sistema numera automaticamente (scheduling, scheduling_2, scheduling_3...).
// O único campo editável é a descrição semântica (o que a IA deve reconhecer).
// O ID técnico (handle do canvas) é gerado e nunca exposto ao admin.
// =============================================================================

const IntentRouterFields = ({ localConfig, handleConfigChange, agentId, nodeId }) => {
  const routerIntents = localConfig.intents || [];

  // ===== Analisador de descrição (botão "Analisar e Melhorar") =====
  const [analyzeState, setAnalyzeState] = useState({
    open: false,
    index: null,
    loading: false,
    error: null,
    analysis: null,
  });

  const closeAnalyzeModal = useCallback(() => {
    setAnalyzeState({ open: false, index: null, loading: false, error: null, analysis: null });
  }, []);

  const runAnalyze = useCallback(async (index) => {
    if (!agentId) {
      setAnalyzeState((s) => ({ ...s, error: 'agentId ausente — recarregue a página' }));
      return;
    }
    const intent = routerIntents[index];
    if (!intent || !intent.id) {
      setAnalyzeState((s) => ({ ...s, error: 'Selecione o tipo da rota antes de analisar' }));
      return;
    }

    setAnalyzeState({ open: true, index, loading: true, error: null, analysis: null });

    try {
      const res = await AgentsFlowService.analyzeIntentDescription(agentId, {
        router_node_id: nodeId,
        intent_id: intent.id,
        all_router_intents: routerIntents.map((i) => ({
          id: i.id,
          key: i.key,
          type: i.type,
          label: i.label,
          description: i.description || '',
        })),
      });
      setAnalyzeState((s) => ({
        ...s,
        loading: false,
        error: null,
        analysis: res.data,
      }));
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Falha ao analisar';
      setAnalyzeState((s) => ({
        ...s,
        loading: false,
        error: msg,
        analysis: null,
      }));
    }
  }, [agentId, nodeId, routerIntents]);

  const applyAnalysisResult = useCallback((newDescription) => {
    if (analyzeState.index === null) return;
    const updated = routerIntents.map((intent, i) =>
      i === analyzeState.index ? { ...intent, description: newDescription } : intent
    );
    handleConfigChange('intents', updated);
    closeAnalyzeModal();
  }, [analyzeState.index, routerIntents, handleConfigChange, closeAnalyzeModal]);

  // --- Catálogo construído a partir de NODE_CATEGORIES ---
  // Fonte de verdade: mesma lista usada na sidebar "Adicionar Nó" do canvas.
  // Assim, só aparecem aqui as capacidades e ações que o admin PODE adicionar
  // ao grafo (não capacidades de behavior, integrations, etc).
  const { capabilityOptions, actionOptions, byKeyType } = useMemo(() => {
    const caps = [];
    const acts = [];

    NODE_CATEGORIES.forEach(cat => {
      (cat.items || []).forEach(item => {
        if (item.type === 'capability') {
          caps.push({
            value: `capability::${item.key}`,
            label: item.label || item.key,
            key: item.key,
            type: 'capability',
          });
        } else if (item.type === 'action') {
          acts.push({
            value: `action::${item.key}`,
            label: item.label || item.key,
            key: item.key,
            type: 'action',
          });
        }
      });
    });

    // Lookup rápido por "type::key" para localizar a option atual no select
    const lookup = {};
    [...caps, ...acts].forEach(opt => { lookup[opt.value] = opt; });

    return {
      capabilityOptions: caps.sort((a, b) => a.label.localeCompare(b.label)),
      actionOptions: acts.sort((a, b) => a.label.localeCompare(b.label)),
      byKeyType: lookup,
    };
  }, []);

  const groupedOptions = useMemo(() => ([
    { label: 'Capacidades', options: capabilityOptions },
    { label: 'Ações', options: actionOptions },
  ]), [capabilityOptions, actionOptions]);

  // --- Geração de id único sequencial baseada na key escolhida ---
  const generateUniqueId = (baseKey) => {
    const usedIds = new Set(routerIntents.map(i => (i.id || '').toLowerCase()));
    if (!usedIds.has(baseKey)) return baseKey;
    let n = 2;
    while (usedIds.has(`${baseKey}_${n}`)) n += 1;
    return `${baseKey}_${n}`;
  };

  // --- Ações ---
  const addIntent = () => {
    // Admin clica "Adicionar Rota" → cria linha vazia, sem tipo selecionado ainda.
    // Ao escolher um tipo no select, geramos o id.
    handleConfigChange('intents', [
      ...routerIntents,
      { id: '', key: '', type: '', label: '', description: '' },
    ]);
  };

  const removeIntent = (index) => {
    handleConfigChange('intents', routerIntents.filter((_, i) => i !== index));
  };

  const updateIntentType = (index, selectedOption) => {
    if (!selectedOption) return;
    const newId = generateUniqueId(selectedOption.key);
    const updated = routerIntents.map((intent, i) =>
      i === index
        ? {
            ...intent,
            id: newId,
            key: selectedOption.key,
            type: selectedOption.type,
            label: selectedOption.label,
          }
        : intent
    );
    handleConfigChange('intents', updated);
  };

  const updateIntentDescription = (index, value) => {
    const updated = routerIntents.map((intent, i) =>
      i === index ? { ...intent, description: value } : intent
    );
    handleConfigChange('intents', updated);
  };

  // react-select styles compatíveis com tamanho "sm" do resto do painel
  const selectStyles = {
    control: (base) => ({ ...base, minHeight: 32, fontSize: 13 }),
    option: (base) => ({ ...base, fontSize: 13, padding: '6px 10px' }),
    groupHeading: (base) => ({
      ...base,
      fontSize: 11,
      fontWeight: 700,
      color: '#6366f1',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    }),
    singleValue: (base) => ({ ...base, fontSize: 13 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label className="afc-form-label">Rotas de Intenção</Form.Label>
        <Form.Text className="afc-form-hint d-block mb-2">
          Cada rota vira um conector de saída no roteador e pode apontar para qualquer nó do grafo.
          Você pode ter <strong>várias rotas do mesmo tipo</strong> (ex: 2 rotas para nós de
          agendamento diferentes) — o sistema numera automaticamente.
        </Form.Text>

        {routerIntents.map((intent, index) => {
          // Encontra a option atual no select com base em key+type.
          const currentOptionKey = intent.type && intent.key
            ? `${intent.type}::${intent.key}`
            : null;
          const currentOption = currentOptionKey ? byKeyType[currentOptionKey] : null;
          const suffix = (() => {
            // Extrai "_2", "_3" etc do id para exibir de forma amigável
            if (!intent.id || !intent.key) return '';
            const m = intent.id.match(new RegExp(`^${intent.key}_(\\d+)$`));
            return m ? ` (${m[1]})` : '';
          })();

          return (
            <div key={index} className="afc-router-intent-item mb-2 p-2 border rounded">
              <div className="d-flex align-items-start gap-2 mb-2">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Form.Label style={{ fontSize: 11, marginBottom: 2, color: '#64748b' }}>
                    Tipo da rota{suffix}
                  </Form.Label>
                  <Select
                    classNamePrefix="afc-select"
                    options={groupedOptions}
                    value={currentOption}
                    onChange={(opt) => updateIntentType(index, opt)}
                    placeholder="Escolha uma capacidade ou ação..."
                    styles={selectStyles}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    isSearchable
                  />
                </div>
                <div style={{ paddingTop: 18 }}>
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => removeIntent(index)}
                    title="Remover rota"
                  >
                    <IconTrash size={14} />
                  </button>
                </div>
              </div>
              <Form.Label style={{ fontSize: 11, marginBottom: 2, color: '#64748b' }}>
                Descrição semântica (o que a IA deve reconhecer para cair nesta rota)
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                size="sm"
                className="afc-form-control"
                value={intent.description || ''}
                onChange={(e) => updateIntentDescription(index, e.target.value)}
                placeholder="Ex: Cliente já possui agendamento e quer mudar data/horário (palavras-chave: reagendar, remarcar, adiar, mudar horário)..."
              />
              <div className="d-flex justify-content-between align-items-center mt-1">
                <small className="text-muted" style={{ fontSize: 11 }}>
                  {(intent.description || '').length} caracteres
                </small>
                <Button
                  size="sm"
                  variant="outline-primary"
                  disabled={
                    !agentId ||
                    !intent.id ||
                    !(intent.description || '').trim() ||
                    (intent.description || '').trim().length < 10 ||
                    (analyzeState.loading && analyzeState.index === index)
                  }
                  onClick={() => runAnalyze(index)}
                  title={
                    !agentId
                      ? 'Salve o agente primeiro'
                      : !intent.id
                        ? 'Escolha o tipo da rota primeiro'
                        : (intent.description || '').trim().length < 10
                          ? 'Escreva uma descrição com pelo menos 10 caracteres'
                          : 'Analisar com IA e sugerir melhorias'
                  }
                  style={{ fontSize: 11, padding: '2px 8px' }}
                >
                  <IconSparkles size={12} className="me-1" />
                  Analisar e Melhorar
                </Button>
              </div>
            </div>
          );
        })}

        <Button
          variant="outline-primary"
          size="sm"
          onClick={addIntent}
          className="mt-1"
        >
          <IconPlus size={14} className="me-1" />
          Adicionar Rota
        </Button>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label className="afc-form-label">Confiança Mínima (%)</Form.Label>
        <Form.Control
          type="number"
          className="afc-form-control"
          value={localConfig.minConfidence ?? 70}
          onChange={(e) => handleConfigChange('minConfidence', parseInt(e.target.value))}
          min={0}
          max={100}
        />
        <Form.Text className="afc-form-hint">
          Se nenhuma intenção atingir essa confiança, a mensagem vai para o Fallback
        </Form.Text>
      </Form.Group>

      {routerIntents.length === 0 && (
        <Alert variant="warning" className="py-2">
          <small>Adicione pelo menos uma rota para o roteador funcionar.</small>
        </Alert>
      )}

      <AnalyzeIntentModal
        isOpen={analyzeState.open}
        onClose={closeAnalyzeModal}
        onApply={applyAnalysisResult}
        intentLabel={
          analyzeState.index !== null && routerIntents[analyzeState.index]
            ? routerIntents[analyzeState.index].id
            : ''
        }
        currentDescription={
          analyzeState.index !== null && routerIntents[analyzeState.index]
            ? (routerIntents[analyzeState.index].description || '')
            : ''
        }
        analysis={analyzeState.analysis}
        loading={analyzeState.loading}
        error={analyzeState.error}
        onRetry={() => analyzeState.index !== null && runAnalyze(analyzeState.index)}
      />
    </>
  );
};


// =============================================================================
// DataCheckFields — verifica 1+ variáveis no estado, AND entre elas
// =============================================================================
//
// Formato persistido: { variables: [{ name, operator, compareValue }] }
// Backwards-compat: aceita formato antigo { variable, operator, compareValue }
// e converte para o novo na primeira edição.
// =============================================================================

// Operadores disponíveis por tipo declarado da variável.
const OPERATORS_BY_TYPE = {
  boolean: [
    { value: 'is_true', label: 'É verdadeiro' },
    { value: 'is_false', label: 'É falso' },
    { value: 'exists', label: 'Existe (foi setada)' },
    { value: 'not_exists', label: 'Não existe' },
  ],
  number: [
    { value: 'equals', label: 'É igual a' },
    { value: 'not_equals', label: 'É diferente de' },
    { value: 'greater_than', label: 'Maior que' },
    { value: 'less_than', label: 'Menor que' },
    { value: 'greater_or_equal', label: 'Maior ou igual' },
    { value: 'less_or_equal', label: 'Menor ou igual' },
    { value: 'exists', label: 'Existe' },
    { value: 'not_exists', label: 'Não existe' },
  ],
  enum: [
    { value: 'equals', label: 'É igual a' },
    { value: 'not_equals', label: 'É diferente de' },
    { value: 'exists', label: 'Existe' },
    { value: 'not_exists', label: 'Não existe' },
  ],
  string: [
    { value: 'exists', label: 'Existe (não vazia)' },
    { value: 'not_exists', label: 'Não existe / vazia' },
    { value: 'equals', label: 'É igual a' },
    { value: 'not_equals', label: 'É diferente de' },
    { value: 'contains', label: 'Contém' },
    { value: 'not_contains', label: 'Não contém' },
    { value: 'is_empty', label: 'Está vazia' },
    { value: 'is_not_empty', label: 'Não está vazia' },
  ],
};

const OPERATORS_NO_VALUE = new Set([
  'exists', 'not_exists', 'is_true', 'is_false', 'is_empty', 'is_not_empty',
]);

const DataCheckFields = ({ localConfig, handleConfigChange, nodes = [], currentNodeId }) => {
  // Coleta variáveis declaradas no grafo (excluindo o próprio data_check).
  const declared = useGraphDeclaredVariables(
    (nodes || []).filter((n) => n.id !== currentNodeId)
  );

  // Mapa nome → metadata (para descobrir tipo e options da variável escolhida)
  const declaredByName = useMemo(() => {
    const map = new Map();
    declared.forEach((v) => {
      if (!map.has(v.name)) map.set(v.name, v);
    });
    return map;
  }, [declared]);

  // Options do dropdown — group por origem.
  const selectOptions = useMemo(() => {
    const byGroup = { extract: [], set_variable: [], collect_field: [] };
    declared.forEach((v) => {
      const list = byGroup[v.source] || [];
      list.push({
        value: v.name,
        label: v.name,
        sublabel: `${v.sourceNodeLabel} (${v.type})`,
        meta: v,
      });
    });
    const groups = [];
    if (byGroup.extract.length) groups.push({ label: 'Extraídas por IA', options: byGroup.extract });
    if (byGroup.set_variable.length) groups.push({ label: 'Definidas por nó', options: byGroup.set_variable });
    if (byGroup.collect_field.length) groups.push({ label: 'Coletadas do cliente', options: byGroup.collect_field });
    return groups;
  }, [declared]);

  // Normaliza config legada (single variable) para o novo formato (array)
  // de forma reativa, sem mutar o config persistido até o usuário interagir.
  const checks = useMemo(() => {
    if (Array.isArray(localConfig.variables) && localConfig.variables.length > 0) {
      return localConfig.variables;
    }
    if (localConfig.variable) {
      return [{
        name: localConfig.variable,
        operator: localConfig.operator || 'exists',
        compareValue: localConfig.compareValue || '',
      }];
    }
    return [];
  }, [localConfig.variables, localConfig.variable, localConfig.operator, localConfig.compareValue]);

  // Persiste mudanças sempre no novo formato e limpa campos legados
  const persist = (newChecks) => {
    handleConfigChange('variables', newChecks);
    if (localConfig.variable !== undefined) handleConfigChange('variable', undefined);
    if (localConfig.operator !== undefined) handleConfigChange('operator', undefined);
    if (localConfig.compareValue !== undefined) handleConfigChange('compareValue', undefined);
  };

  const updateCheck = (index, field, value) => {
    const next = checks.map((c, i) => {
      if (i !== index) return c;
      const updated = { ...c, [field]: value };
      // Ao trocar a variável, o tipo (e portanto operador/valor válidos) muda;
      // resetar operator e compareValue para evitar combinação inconsistente.
      if (field === 'name') {
        updated.operator = 'exists';
        updated.compareValue = '';
      }
      return updated;
    });
    persist(next);
  };

  const addCheck = () => {
    persist([...checks, { name: '', operator: 'exists', compareValue: '' }]);
  };

  const removeCheck = (index) => {
    persist(checks.filter((_, i) => i !== index));
  };

  const resolveTypeFor = (name) => {
    const meta = declaredByName.get(name);
    return meta?.type || 'string';
  };

  return (
    <>
      <div className="afc-capability-description mb-3">
        <small className="text-muted">
          Verifica uma ou mais variáveis no estado. Todas precisam passar (AND) para o caminho{' '}
          <strong>Verdadeiro</strong>. Caso alguma falhe, segue por <strong>Falso</strong> e a lista de
          faltantes fica em <code>{'{{_missing_variables}}'}</code> para uso em mensagens.
        </small>
      </div>

      {checks.length === 0 && (
        <Alert variant="light" className="mb-3 small">
          Nenhuma verificação configurada — adicione ao menos uma variável.
        </Alert>
      )}

      {checks.map((check, index) => {
        const type = resolveTypeFor(check.name);
        const operators = OPERATORS_BY_TYPE[type] || OPERATORS_BY_TYPE.string;
        const currentOption = check.name
          ? { value: check.name, label: check.name }
          : null;
        const meta = declaredByName.get(check.name);
        const showCompareValue = !OPERATORS_NO_VALUE.has(check.operator);
        const enumOptions = type === 'enum' && Array.isArray(meta?.options) ? meta.options : [];

        return (
          <div
            key={index}
            className="afc-data-check-row mb-3 p-2"
            style={{ border: '1px solid #e5e7eb', borderRadius: 6, background: '#fafafa' }}
          >
            <div className="d-flex justify-content-between align-items-start mb-2">
              <small className="text-muted">Verificação #{index + 1}</small>
              <Button
                size="sm"
                variant="link"
                className="text-danger p-0"
                onClick={() => removeCheck(index)}
                title="Remover"
              >
                <IconTrash size={14} />
              </Button>
            </div>

            <Form.Group className="mb-2">
              <Form.Label className="afc-form-label small mb-1">Variável</Form.Label>
              <CreatableSelect
                isClearable
                options={selectOptions}
                value={currentOption}
                onChange={(opt) => updateCheck(index, 'name', opt?.value || '')}
                placeholder="Selecione ou digite o nome..."
                formatOptionLabel={(option) =>
                  option.sublabel ? (
                    <div>
                      <div style={{ fontWeight: 500 }}>{option.label}</div>
                      <small className="text-muted">{option.sublabel}</small>
                    </div>
                  ) : (
                    option.label
                  )
                }
                noOptionsMessage={() =>
                  declared.length === 0
                    ? 'Nenhuma variável declarada no grafo ainda — digite o nome manualmente.'
                    : 'Nada encontrado'
                }
                formatCreateLabel={(input) => `Usar "${input}" mesmo assim`}
                styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }}
              />
              {meta && (
                <Form.Text className="text-muted">
                  Tipo: <strong>{meta.type}</strong> • Origem: {meta.sourceNodeLabel}
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label className="afc-form-label small mb-1">Operador</Form.Label>
              <Form.Select
                size="sm"
                className="afc-form-control"
                value={check.operator || 'exists'}
                onChange={(e) => updateCheck(index, 'operator', e.target.value)}
              >
                {operators.map((op) => (
                  <option key={op.value} value={op.value}>
                    {op.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {showCompareValue && (
              <Form.Group className="mb-2">
                <Form.Label className="afc-form-label small mb-1">Valor para comparar</Form.Label>
                {type === 'enum' && enumOptions.length > 0 ? (
                  <Form.Select
                    size="sm"
                    className="afc-form-control"
                    value={check.compareValue || ''}
                    onChange={(e) => updateCheck(index, 'compareValue', e.target.value)}
                  >
                    <option value="">— escolha —</option>
                    {enumOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </Form.Select>
                ) : (
                  <Form.Control
                    type={type === 'number' ? 'number' : 'text'}
                    size="sm"
                    className="afc-form-control"
                    value={check.compareValue || ''}
                    onChange={(e) => updateCheck(index, 'compareValue', e.target.value)}
                    placeholder="Valor ou {{outra_variavel}}"
                  />
                )}
              </Form.Group>
            )}
          </div>
        );
      })}

      <Button size="sm" variant="outline-primary" onClick={addCheck}>
        <IconPlus size={13} className="me-1" />
        Adicionar verificação
      </Button>
    </>
  );
};

export default ConditionFields;
